import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  defaultPreferences,
  isDyslexiaTheme,
  type ThemePreferences,
} from './theme';

// ---------------------------------------------------------------------------
// LocalStorage keys
// ---------------------------------------------------------------------------

const LS_KEYS = {
  mode: 'cadence_theme',
  highContrast: 'cadence_high_contrast',
  fontFamily: 'cadence_font_family',
  fontSize: 'cadence_font_size',
  reducedMotion: 'cadence_reduced_motion',
} as const;

function readPreferences(): ThemePreferences {
  try {
    return {
      mode:
        (localStorage.getItem(LS_KEYS.mode) as ThemePreferences['mode']) ??
        defaultPreferences.mode,
      highContrast: localStorage.getItem(LS_KEYS.highContrast) === 'true',
      fontFamily:
        ((() => {
          const stored = localStorage.getItem(LS_KEYS.fontFamily);
          // Migrate legacy 'dyslexic' value to the current 'dyslexia' slug.
          if (stored === 'dyslexic') return 'dyslexia';
          return stored as ThemePreferences['fontFamily'];
        })()) ??
        defaultPreferences.fontFamily,
      fontSize:
        (localStorage.getItem(LS_KEYS.fontSize) as ThemePreferences['fontSize']) ??
        defaultPreferences.fontSize,
      reducedMotion: localStorage.getItem(LS_KEYS.reducedMotion) === 'true',
    };
  } catch {
    return defaultPreferences;
  }
}

function writePreferences(prefs: ThemePreferences): void {
  try {
    localStorage.setItem(LS_KEYS.mode, prefs.mode);
    localStorage.setItem(LS_KEYS.highContrast, String(prefs.highContrast));
    localStorage.setItem(LS_KEYS.fontFamily, prefs.fontFamily);
    localStorage.setItem(LS_KEYS.fontSize, prefs.fontSize);
    localStorage.setItem(LS_KEYS.reducedMotion, String(prefs.reducedMotion));
  } catch {
    // Silently ignore quota-exceeded / private-browsing failures.
  }
}

// Atkinson Hyperlegible ships in @fontsource/atkinson-hyperlegible. The
// dynamic import keeps the dyslexia font weights out of the always-loaded
// bundle so the executive majority — who use the default Inter face — don't
// pay for the ~40 KB the dyslexia weights add. Tracked-loaded so a second
// `ensureDyslexiaFont(true)` call doesn't re-import on every preference
// change.
let dyslexiaFontLoaded = false;

function ensureDyslexiaFont(enabled: boolean): void {
  if (enabled && !dyslexiaFontLoaded) {
    dyslexiaFontLoaded = true;
    // No-await — the @fontsource css modules just register @font-face rules
    // when their JS runs; the rendered DOM picks them up the moment the
    // browser parses them. If the dynamic import fails (offline / blocked
    // CDN) the dyslexia face simply doesn't load and the user keeps Inter.
    Promise.all([
      import('@fontsource/atkinson-hyperlegible/400.css'),
      import('@fontsource/atkinson-hyperlegible/700.css'),
    ]).catch(() => {
      dyslexiaFontLoaded = false;
    });
  }
}

// Pixel font sizes for the body's root rem. shadcn / Tailwind read sizes off
// `rem`, so adjusting <html>'s font-size globally re-scales the whole UI.
const FONT_SIZE_PX: Record<ThemePreferences['fontSize'], number> = {
  small: 13,
  medium: 15,
  large: 17,
  xl: 19,
};

interface ThemePreferencesContextValue {
  preferences: ThemePreferences;
  setPreferences: (prefs: ThemePreferences) => void;
  resolvedMode: 'dark' | 'light';
}

export const ThemePreferencesContext = createContext<
  ThemePreferencesContextValue | undefined
>(undefined);

export function CadenceThemeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [preferences, setPreferencesState] = useState<ThemePreferences>(readPreferences);

  const [systemDark, setSystemDark] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const resolvedMode: 'dark' | 'light' = useMemo(() => {
    if (preferences.mode === 'system') return systemDark ? 'dark' : 'light';
    return preferences.mode;
  }, [preferences.mode, systemDark]);

  useEffect(() => {
    ensureDyslexiaFont(isDyslexiaTheme(preferences.fontFamily));
  }, [preferences.fontFamily]);

  // Toggle the `dark` class on <html> so every shadcn primitive picks up the
  // resolved palette. Also write `font-size` so the size preference cascades
  // via rem, and a `font-dyslexia` flag so a single CSS rule in index.css can
  // swap the body font when the dyslexia themes are active.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolvedMode === 'dark');
    root.classList.toggle('font-dyslexia', isDyslexiaTheme(preferences.fontFamily));
    root.classList.toggle('high-contrast', preferences.highContrast);
    root.classList.toggle('reduced-motion', preferences.reducedMotion);
    root.style.fontSize = `${FONT_SIZE_PX[preferences.fontSize]}px`;
  }, [resolvedMode, preferences.fontFamily, preferences.highContrast, preferences.reducedMotion, preferences.fontSize]);

  const setPreferences = useCallback((prefs: ThemePreferences) => {
    writePreferences(prefs);
    setPreferencesState(prefs);
  }, []);

  const contextValue = useMemo<ThemePreferencesContextValue>(
    () => ({ preferences, setPreferences, resolvedMode }),
    [preferences, setPreferences, resolvedMode],
  );

  return (
    <ThemePreferencesContext.Provider value={contextValue}>
      {children}
    </ThemePreferencesContext.Provider>
  );
}

export function useThemePreferences(): ThemePreferencesContextValue {
  const ctx = useContext(ThemePreferencesContext);
  if (!ctx) {
    throw new Error(
      'useThemePreferences must be used within a CadenceThemeProvider',
    );
  }
  return ctx;
}
